import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { requestNotificationPermission, isNotificationSupported } from "../lib/firebase";

const PushNotificationToggle = () => {
  const { token } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(isNotificationSupported());
    checkStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/push/status`, { headers });
      setIsEnabled(response.data.registered);
    } catch (error) {
      console.error("Error checking push status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  };

  const enableNotifications = async () => {
    if (!isSupported) {
      toast.error("Brskalnik ne podpira push obvestil");
      return;
    }

    setIsLoading(true);
    try {
      // Ensure SW is registered before requesting FCM token
      await registerServiceWorker();

      // Use central helper: it handles Notification.requestPermission + getToken with VAPID
      const fcmToken = await requestNotificationPermission();

      if (!fcmToken) {
        // Distinguish between denied permission and other failures
        if (typeof Notification !== "undefined" && Notification.permission === "denied") {
          toast.error("Obvestila so bila zavrnjena. Omogočite jih v nastavitvah brskalnika (zaklepenček pri naslovu → Obvestila → Dovoli).");
        } else if (typeof Notification !== "undefined" && Notification.permission === "default") {
          toast.error("Dovoljenje ni bilo odobreno. Poskusite znova.");
        } else {
          toast.error("Napaka pri pridobivanju FCM žetona. Preverite konzolo brskalnika.");
        }
        setIsLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/fcm/register`, { token: fcmToken }, { headers });
      setIsEnabled(true);
      toast.success("Push obvestila omogočena!");
    } catch (error) {
      console.error("Error enabling notifications:", error);
      const detail = error.response?.data?.detail;
      toast.error(detail || "Napaka pri omogočanju obvestil");
    } finally {
      setIsLoading(false);
    }
  };

  const disableNotifications = async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/fcm/unregister`, { headers });
      setIsEnabled(false);
      toast.success("Push obvestila onemogočena");
    } catch (error) {
      console.error("Error disabling notifications:", error);
      toast.error("Napaka pri onemogočanju obvestil");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <Button variant="outline" size="sm" disabled className="opacity-50">
        <BellOff className="h-4 w-4 mr-2" />
        Obvestila niso podprta
      </Button>
    );
  }

  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Nalagam...
      </Button>
    );
  }

  return (
    <Button
      variant={isEnabled ? "default" : "outline"}
      size="sm"
      onClick={isEnabled ? disableNotifications : enableNotifications}
      data-testid="push-toggle-btn"
    >
      {isEnabled ? (
        <>
          <Bell className="h-4 w-4 mr-2" />
          Obvestila ON
        </>
      ) : (
        <>
          <BellOff className="h-4 w-4 mr-2" />
          Omogoči obvestila
        </>
      )}
    </Button>
  );
};

export default PushNotificationToggle;
